import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import SuccessPopup from '../shared/SuccessPopup';

const HouseholdInformation = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Auto-fill mother/father info when "Same as Primary" is checked/unchecked
  useEffect(() => {
    if (formData.mother_same_as_primary) {
      setFormData(prev => ({
        ...prev,
        mother_guardian_first_name: prev.primary_parent_first_name || '',
        mother_guardian_last_name: prev.primary_parent_last_name || '',
        mother_home_phone: prev.primary_parent_home_phone || '',
        mother_cell_phone: prev.primary_parent_cell_phone || '',
        mother_employer_phone: prev.primary_parent_employer_phone || ''
      }));
    } else {
      // Clear fields when unchecked
      setFormData(prev => ({
        ...prev,
        mother_guardian_first_name: '',
        mother_guardian_last_name: '',
        mother_home_phone: '',
        mother_cell_phone: '',
        mother_employer_phone: ''
      }));
    }
  }, [formData.mother_same_as_primary, formData.primary_parent_first_name, formData.primary_parent_last_name, formData.primary_parent_home_phone, formData.primary_parent_cell_phone, formData.primary_parent_employer_phone]);

  useEffect(() => {
    if (formData.father_same_as_primary) {
      setFormData(prev => ({
        ...prev,
        father_guardian_first_name: prev.primary_parent_first_name || '',
        father_guardian_last_name: prev.primary_parent_last_name || '',
        father_home_phone: prev.primary_parent_home_phone || '',
        father_cell_phone: prev.primary_parent_cell_phone || '',
        father_employer_phone: prev.primary_parent_employer_phone || ''
      }));
    } else {
      // Clear fields when unchecked
      setFormData(prev => ({
        ...prev,
        father_guardian_first_name: '',
        father_guardian_last_name: '',
        father_home_phone: '',
        father_cell_phone: '',
        father_employer_phone: ''
      }));
    }
  }, [formData.father_same_as_primary, formData.primary_parent_first_name, formData.primary_parent_last_name, formData.primary_parent_home_phone, formData.primary_parent_cell_phone, formData.primary_parent_employer_phone]);

  useEffect(() => {
    if (user && !dataLoaded) {
      fetchHouseholdData();
    }
  }, [user, dataLoaded]);

  const fetchHouseholdData = async () => {
    try {
      const response = await axios.get('/api/form-submissions/household-information');
      setFormData(response.data || {});
      setDataLoaded(true);
    } catch (error) {
      console.error('Failed to fetch household information:', error);
      setFormData({});
      setDataLoaded(true);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await axios.post('/api/form-submissions/household-information', {
        household_data: formData
      });
      setSuccess(true);
    } catch (error) {
      console.error('Failed to save household information:', error);
      setError(error.response?.data?.error || 'Failed to save household information');
    } finally {
      setLoading(false);
    }
  };

  if (!dataLoaded) {
    return <div>Loading household information...</div>;
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
      <h2>Household Information</h2>
      <p style={{ marginBottom: '2rem', color: '#666' }}>
        This information will be automatically used to pre-fill forms when enrolling your students.
        Please keep it up to date.
      </p>

      {error && <div style={{ color: 'red', marginBottom: '1rem', padding: '0.5rem', border: '1px solid red', borderRadius: '4px' }}>{error}</div>}

      <SuccessPopup
        show={success}
        onClose={() => setSuccess(false)}
        title="Success!"
        message="Household information saved successfully!"
        buttonText="OK"
        autoCloseDelay={5000}
      />

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Primary Parent Information</h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Primary Parent First Name
            </label>
            <input
              type="text"
              name="primary_parent_first_name"
              value={formData.primary_parent_first_name || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Primary Parent Last Name
            </label>
            <input
              type="text"
              name="primary_parent_last_name"
              value={formData.primary_parent_last_name || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Primary Parent Home Phone
            </label>
            <input
              type="tel"
              name="primary_parent_home_phone"
              value={formData.primary_parent_home_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Primary Parent Cell Phone
            </label>
            <input
              type="tel"
              name="primary_parent_cell_phone"
              value={formData.primary_parent_cell_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Primary Parent Employer Phone
            </label>
            <input
              type="tel"
              name="primary_parent_employer_phone"
              value={formData.primary_parent_employer_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Primary Parent Email
            </label>
            <input
              type="email"
              name="primary_parent_email"
              value={formData.primary_parent_email || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Mother/Guardian Information</h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'inline-block', marginRight: '1rem', fontWeight: '500' }}>
              <input
                type="checkbox"
                name="mother_same_as_primary"
                checked={formData.mother_same_as_primary || false}
                onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.checked } })}
                style={{ marginRight: '0.5rem' }}
              />
              Same as Primary Parent
            </label>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Mother/Guardian First Name
            </label>
            <input
              type="text"
              name="mother_guardian_first_name"
              value={formData.mother_guardian_first_name || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Mother/Guardian Last Name
            </label>
            <input
              type="text"
              name="mother_guardian_last_name"
              value={formData.mother_guardian_last_name || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Mother/Guardian Home Phone
            </label>
            <input
              type="tel"
              name="mother_home_phone"
              value={formData.mother_home_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Mother/Guardian Cell Phone
            </label>
            <input
              type="tel"
              name="mother_cell_phone"
              value={formData.mother_cell_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Mother/Guardian Employer Phone
            </label>
            <input
              type="tel"
              name="mother_employer_phone"
              value={formData.mother_employer_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Father/Guardian Information</h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'inline-block', marginRight: '1rem', fontWeight: '500' }}>
              <input
                type="checkbox"
                name="father_same_as_primary"
                checked={formData.father_same_as_primary || false}
                onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.checked } })}
                style={{ marginRight: '0.5rem' }}
              />
              Same as Primary Parent
            </label>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Father/Guardian First Name
            </label>
            <input
              type="text"
              name="father_guardian_first_name"
              value={formData.father_guardian_first_name || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Father/Guardian Last Name
            </label>
            <input
              type="text"
              name="father_guardian_last_name"
              value={formData.father_guardian_last_name || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Father/Guardian Home Phone
            </label>
            <input
              type="tel"
              name="father_home_phone"
              value={formData.father_home_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Father/Guardian Cell Phone
            </label>
            <input
              type="tel"
              name="father_cell_phone"
              value={formData.father_cell_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Father/Guardian Employer Phone
            </label>
            <input
              type="tel"
              name="father_employer_phone"
              value={formData.father_employer_phone || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Home Address</h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Street Address
            </label>
            <input
              type="text"
              name="home_street_address"
              value={formData.home_street_address || ''}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                City
              </label>
              <input
                type="text"
                name="home_city"
                value={formData.home_city || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                County
              </label>
              <input
                type="text"
                name="home_county"
                value={formData.home_county || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                State
              </label>
              <input
                type="text"
                name="home_state"
                value={formData.home_state || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Zip Code
              </label>
              <input
                type="text"
                name="home_zip_code"
                value={formData.home_zip_code || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Emergency Contacts</h3>

          <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #eee', borderRadius: '4px' }}>
            <h4 style={{ marginTop: '0', marginBottom: '1rem', color: '#34495e' }}>Emergency Contact 1</h4>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Name
              </label>
              <input
                type="text"
                name="emergency_contact_1_name"
                value={formData.emergency_contact_1_name || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Home Phone
              </label>
              <input
                type="tel"
                name="emergency_contact_1_home_phone"
                value={formData.emergency_contact_1_home_phone || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Cell/Work Phone
              </label>
              <input
                type="tel"
                name="emergency_contact_1_cell_work_phone"
                value={formData.emergency_contact_1_cell_work_phone || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #eee', borderRadius: '4px' }}>
            <h4 style={{ marginTop: '0', marginBottom: '1rem', color: '#34495e' }}>Emergency Contact 2</h4>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Name
              </label>
              <input
                type="text"
                name="emergency_contact_2_name"
                value={formData.emergency_contact_2_name || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Home Phone
              </label>
              <input
                type="tel"
                name="emergency_contact_2_home_phone"
                value={formData.emergency_contact_2_home_phone || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Cell/Work Phone
              </label>
              <input
                type="tel"
                name="emergency_contact_2_cell_work_phone"
                value={formData.emergency_contact_2_cell_work_phone || ''}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              padding: '0.75rem 2rem',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500'
            }}
          >
            {loading ? 'Saving...' : 'Save Household Information'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HouseholdInformation;
